import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award } from 'lucide-react';
import { publicAPI, trainerAPI } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from "@/contexts/AuthContext";
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import VideoUpload from "@/components/VideoUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import MultiCheck	 from "@/components/MultiCheck";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import Section from "@/components/Section";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

const DISCIPLINES = [
	'dressage', 'show_jumping', 'vaulting', 'trail', 'western', 'classical', 'therapy',
	'natural_horsemanship', 'liberty', 'groundwork', 'colt_starting', 'young_horse', 'sports_therapy'
];
const SERVICES = ['own_yard', 'client_yard', 'clinics', 'competition_prep', 'sales_prep', 'rider_coaching'];
const PRICING_UNITS = ['lesson', 'hour', 'month', 'package'];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateTrainerAdPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const { user } = useAuth();
	const [images, setImages] = useState([]);
	const [videos, setVideos] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		pricing_unit: 'lesson',
		disciplines: [],
		services: [],
		experience_years: '',
		certifications: '',
		travels_to_client: false,
		company_name: '',
		website: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: [],
		hidden_videos: []
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			trainerAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					pricing_unit: ad.pricing_unit || 'month',
					disciplines: ad.disciplines || [],
					services: ad.services || [],
					experience_years: ad.experience_years || '',
					certifications: ad.certifications || '',
					travels_to_client: ad.travels_to_client || false,
					company_name: ad.company_name || '',
					website: ad.website || '',
					contact_phone: ad.contact_phone || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					hidden_videos: ad.hidden_videos || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
					included_videos: ad.included_videos || pricing.free_videos_limit || 1,
				});

				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);
				// Load existing videos
				const existingVideos = (ad.videos || []).map(url => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					enabled: !(ad.hidden_videos || []).includes(url),
					isExisting: true,
					name: url.split('/').pop()
				}));
				// need to remove videos in case of cloning as I don't have the objects to upload
				setVideos(isClone ? [] : existingVideos);
			}).catch((e) => {
				console.log(e)
				navigate('/dashboard')
			});
		}
	}, [user, editId, navigate]);

	const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

	const toggleIn = (key, value) => {
		setForm((p) => ({
			...p,
			[key]: p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value],
		}));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) => setImages(prev => prev.map((img, idx) =>
		idx === i ? { ...img, enabled: !img.enabled } : img));

	// --- Video handlers ---
	const handleVideos = (e) => {
		const files = Array.from(e.target.files);
		const maxSize = (pricing.max_video_size_mb || 50) * 1024 * 1024;
		for (const f of files) {
			if (f.size > maxSize) {
				toast.error(t('horse.video_too_large', { size: pricing.max_video_size_mb }));
				return;
			}
		}
		const newVids = files.map(f =>
			({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false, name: f.name })
		);
		setVideos(prev => [...prev, ...newVids]);
	};
	const removeVideo = (i) => {
		if (!videos[i].isExisting) URL.revokeObjectURL(videos[i].preview);
		setVideos(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleVideo = (i) => setVideos(prev =>
		prev.map((v, idx) => idx === i ? { ...v, enabled: !v.enabled } : v)
	);

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.price) {
			toast.error(t('common.fill_required'));
			return;
		}

		const cost = computeCosts(images, videos, pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);
		const newVideos = cost.enabledVideos.filter(v => !v.isExisting);

		// Compute hidden lists from existing media
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);
		const hiddenVids = videos.filter(v => v.isExisting && !v.enabled).map(v => v.url);

		const payload = {
			...form,
			price: parseFloat(form.price),
			experience_years: form.experience_years ? parseInt(form.experience_years, 10) : null,
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			included_videos: Math.max(pricing.free_videos_limit, cost.enabledVideos.length),
			hidden_images: hiddenImgs,
			hidden_videos: hiddenVids,
		};

		await handleSubmit(payload, trainerAPI, isClone ? null : editId, 'trainer', cost,
			newImages, newVideos, form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-trainer-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/trainers" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<Award className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('trainer.create_title') : editId ? t('trainer.edit_title') :
							t('trainer.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('trainer.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"
							data-testid="trainer-form">
					<Section title={t('horse.basic_info')}>
						<div className="mb-4">
							<Label>{t('horse.title_label')} *</Label>
							<Input value={form.title} onChange={(e) => set('title', e.target.value)}
										 required maxLength={120} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
										 data-testid="trainer-title" />
						</div>

						<div className="mb-4">
							<Label>{t('horse.description')} *</Label>
							<Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
												required rows={5} maxLength={4000}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
												data-testid="trainer-description" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('horse.price')} (€) *</Label>
								<Input type="number" min={0} step="any" value={form.price}
											 onChange={(e) => set('price', e.target.value)} required
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="trainer-price" />
							</div>
							<div>
								<Label>{t('transport.pricing_unit')}</Label>
								<Select value={form.pricing_unit} onValueChange={(v) => set('pricing_unit', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>{PRICING_UNITS.map(u =>
										<SelectItem key={u} value={u}>{t(`school.unit.${u}`)}</SelectItem>)}
									</SelectContent>
								</Select>
							</div>
						</div>
					</Section>

					<Section title={t('horse.details')}>
						<div className="mb-4">
							<MultiCheck label={t('trainer.specialisations')} items={DISCIPLINES} formKey="disciplines"
												prefix="trainer.discipline_opt" form={ form } toggleIn={ toggleIn } t={ t } />
						</div>
						<div className="mb-4">
							<MultiCheck label={t('trainer.services_offered')} items={SERVICES} formKey="services"
													prefix="trainer.service_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('trainer.experience_years')}</Label>
								<Input type="number" min={0} max={80} value={form.experience_years}
											 onChange={(e) => set('experience_years', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="trainer-experience" />
							</div>
							<label className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none mt-8">
								<Checkbox checked={form.travels_to_client} onCheckedChange={(v) => set('travels_to_client', !!v)}
													data-testid="trainer-travels" />
								{t('trainer.travels_to_client')}
							</label>
						</div>

						<div className="mb-4">
							<Label>{t('school.certifications')}</Label>
							<Textarea value={form.certifications} onChange={(e) => set('certifications', e.target.value)}
												rows={2} maxLength={1000} placeholder="FEI, Parelli, EASI..."
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('transport.company_name')}</Label>
								<Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
							<div>
								<Label>{t('school.website')}</Label>
								<Input value={form.website} onChange={(e) => set('website', e.target.value)}
											 placeholder="https://..." className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div>
							<Label>{t('horse.contact_phone')}</Label>
							<Input value={form.contact_phone}
										 onChange={(e) => set('contact_phone', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
					</Section>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ set }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Videos */}
					<VideoUpload videos={ videos }
											 pricing={ pricing }
											 handleVideos={ handleVideos }
											 removeVideo={ removeVideo }
											 toggleVideo={ toggleVideo } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ set } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						videos={videos}
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					<WaiverCheckBox
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='trainer.publish_btn'
						images={ images }
						videos={ videos }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

				</form>
			</div>
		</div>
	);
}
